import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  GraduationCap, MapPin, Phone, Banknote, Save, MessageSquare,
  CheckCircle2, BookOpen, Languages, Clock, AlertCircle, CalendarCheck, TrendingUp, FileSignature,
  ClipboardCheck,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import SessionPointageDialog from "../components/SessionPointageDialog";
import SessionsList from "../components/SessionsList";
import { useAuth, API, formatApiErrorDetail } from "../context/AuthContext";
import { useFmtXof } from "../lib/i18nFmt";

const splitList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const joinList = (a) => (a || []).join(", ");

export default function EducatorDashboard() {
  const { t } = useTranslation();
  const fmtXof = useFmtXof();
  const { user, refreshMe } = useAuth();
  const p = user?.profile || {};

  const [form, setForm] = useState({
    bio: p.bio || "",
    phone: p.phone || "",
    city: p.city || "",
    subjects: joinList(p.subjects),
    levels: joinList(p.levels),
    experience_years: p.experience_years || 0,
    hourly_rate: p.hourly_rate || 0,
    education: p.education || "",
    languages: joinList(p.languages),
    availability: p.availability || "",
  });
  const [saving, setSaving] = useState(false);
  const [requests, setRequests] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [sessionDialogOpen, setSessionDialogOpen] = useState(false);
  const [sessionsRefreshKey, setSessionsRefreshKey] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/contact-request/received`).then(({ data }) => setRequests(data.items || [])).catch(() => {});
    axios.get(`${API}/bookings`).then(({ data }) => setBookings(data.items || [])).catch(() => {});
  }, []);

  const totalEarned = bookings.filter((b) => b.status === "PAID").reduce((s, b) => s + (b.educator_net_xof || 0), 0);
  const pendingCount = bookings.filter((b) => b.status === "PENDING_PAYMENT").length;
  const fmt = fmtXof;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      await axios.put(`${API}/profile/educator`, {
        phone: form.phone || null,
        city: form.city || null,
        bio: form.bio,
        subjects: splitList(form.subjects),
        levels: splitList(form.levels),
        experience_years: Number(form.experience_years) || 0,
        hourly_rate: Number(form.hourly_rate) || 0,
        education: form.education,
        languages: splitList(form.languages),
        availability: form.availability,
      });
      await refreshMe();
      toast.success(t("educatorDash.saveSuccess"));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAFCFB]" data-testid="educator-dashboard">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero */}
        <div className="rounded-3xl bg-white border border-[#E5EBE7] p-8 sm:p-10 relative overflow-hidden">
          <span className="geo-shape bg-[#16A34A]/15 h-64 w-64 -top-20 -left-20" aria-hidden />
          <div className="relative flex items-start justify-between flex-wrap gap-4">
            <div>
              <span className="pill"><GraduationCap className="h-3.5 w-3.5" /> {t("educatorDash.pill")}</span>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-[#0A2518]">
                {t("educatorDash.greeting", { name: user?.name?.split(" ")[0] })}
              </h1>
              <p className="mt-3 text-[#4B5563] max-w-2xl">{t("educatorDash.intro")}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge className={`${p.verified ? "bg-[#F0FDF4] text-[#15803D] border-[#16A34A]/30" : "bg-yellow-50 text-yellow-700 border-yellow-200"} border`}>
                {p.verified ? <><CheckCircle2 className="h-3 w-3 mr-1 inline" />{t("educatorDash.verifiedBadge")}</> : t("educatorDash.pendingBadge")}
              </Badge>
              <span className="text-xs text-[#4B5563]">{t("educatorDash.ratingLabel", { rating: (p.rating ?? 5).toFixed(1) })}</span>
            </div>
          </div>
          {!user?.legal_acceptance?.accepted_at && (
            <div className="relative mt-5 rounded-xl bg-yellow-50 border border-yellow-200 p-4 text-sm flex items-start gap-3" data-testid="educator-contract-banner">
              <FileSignature className="h-5 w-5 text-yellow-700 mt-0.5 shrink-0" />
              <div className="flex-1">
                <strong className="text-yellow-800">{t("educatorDash.contractBannerStrong")}</strong>
                <span className="text-yellow-800">{t("educatorDash.contractBanner")}</span>
              </div>
              <Button asChild size="sm" className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white">
                <Link to="/legal/educators">{t("educatorDash.contractSign")}</Link>
              </Button>
            </div>
          )}
        </div>

        {/* Pointage électronique */}
        <Card className="mt-8 p-7 border-[#E5EBE7]" data-testid="educator-pointage-card">
          <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
            <div>
              <h2 className="font-display text-2xl font-semibold text-[#0A2518] flex items-center gap-2">
                <ClipboardCheck className="h-5 w-5 text-[#16A34A]" /> {t("educatorDash.pointageTitle")}
              </h2>
              <p className="text-sm text-[#4B5563] mt-1">{t("educatorDash.pointageHelp")}</p>
            </div>
            <Button
              onClick={() => setSessionDialogOpen(true)}
              className="rounded-full bg-[#16A34A] hover:bg-[#15803D] text-white"
              data-testid="educator-open-pointage-btn"
            >
              <ClipboardCheck className="h-4 w-4 mr-2" /> {t("educatorDash.openPointage")}
            </Button>
          </div>

          <SessionsList key={sessionsRefreshKey} mode="educator" />
        </Card>

        {/* Earnings overview */}
        <div className="mt-8 grid sm:grid-cols-3 gap-4" data-testid="educator-earnings">
          <Card className="p-5 border-[#E5EBE7] rounded-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#16A34A] font-semibold">
              <TrendingUp className="h-4 w-4" /> {t("educatorDash.kpiEarnings")}
            </div>
            <div className="mt-2 font-display text-3xl font-bold text-[#0A2518]" data-testid="earnings-total">{fmt(totalEarned)}</div>
            <div className="text-xs text-[#4B5563] mt-1">{t("educatorDash.kpiEarningsSub")}</div>
          </Card>
          <Card className="p-5 border-[#E5EBE7] rounded-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#16A34A] font-semibold">
              <CalendarCheck className="h-4 w-4" /> {t("educatorDash.kpiPaid")}
            </div>
            <div className="mt-2 font-display text-3xl font-bold text-[#0A2518]">{bookings.filter((b) => b.status === "PAID").length}</div>
            <div className="text-xs text-[#4B5563] mt-1">{t("educatorDash.kpiPaidSub")}</div>
          </Card>
          <Card className="p-5 border-[#E5EBE7] rounded-2xl">
            <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-[#16A34A] font-semibold">
              <Clock className="h-4 w-4" /> {t("educatorDash.kpiPending")}
            </div>
            <div className="mt-2 font-display text-3xl font-bold text-[#0A2518]">{pendingCount}</div>
            <div className="text-xs text-[#4B5563] mt-1">{t("educatorDash.kpiPendingSub")}</div>
          </Card>
        </div>

        {/* Main grid */}
        <div className="mt-10 grid lg:grid-cols-3 gap-6">
          {/* Form */}
          <Card className="lg:col-span-2 p-7 border-[#E5EBE7]" data-testid="educator-profile-card">
            <h2 className="font-display text-2xl font-semibold text-[#0A2518] mb-5">{t("educatorDash.profileTitle")}</h2>
            <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <Label className="text-[#0A2518]">{t("educatorDash.bioLabel")}</Label>
                <Textarea name="bio" value={form.bio} onChange={onChange} className="mt-1.5 min-h-[120px] focus-visible:ring-[#16A34A]" placeholder={t("educatorDash.bioPh")} data-testid="educator-bio-input" />
              </div>

              <div>
                <Label className="text-[#0A2518] flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {t("educatorDash.phoneLabel")}</Label>
                <Input name="phone" value={form.phone} onChange={onChange} className="mt-1.5 h-11" data-testid="educator-phone-input" />
              </div>
              <div>
                <Label className="text-[#0A2518] flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> {t("educatorDash.cityLabel")}</Label>
                <Input name="city" value={form.city} onChange={onChange} className="mt-1.5 h-11" data-testid="educator-city-input" />
              </div>

              <div className="md:col-span-2">
                <Label className="text-[#0A2518] flex items-center gap-1.5"><BookOpen className="h-3.5 w-3.5" /> {t("educatorDash.subjectsLabel")}</Label>
                <Input name="subjects" value={form.subjects} onChange={onChange} className="mt-1.5 h-11" placeholder={t("educatorDash.subjectsPh")} data-testid="educator-subjects-input" />
                <p className="mt-1 text-xs text-[#4B5563]">{t("educatorDash.subjectsHint")}</p>
              </div>
              <div className="md:col-span-2">
                <Label className="text-[#0A2518]">{t("educatorDash.levelsLabel")}</Label>
                <Input name="levels" value={form.levels} onChange={onChange} className="mt-1.5 h-11" placeholder={t("educatorDash.levelsPh")} data-testid="educator-levels-input" />
              </div>

              <div>
                <Label className="text-[#0A2518]">{t("educatorDash.experienceLabel")}</Label>
                <Input name="experience_years" type="number" min={0} value={form.experience_years} onChange={onChange} className="mt-1.5 h-11" data-testid="educator-experience-input" />
              </div>
              <div>
                <Label className="text-[#0A2518] flex items-center gap-1.5"><Banknote className="h-3.5 w-3.5" /> {t("educatorDash.rateLabel")}</Label>
                <Input name="hourly_rate" type="number" min={0} step="500" value={form.hourly_rate} onChange={onChange} className="mt-1.5 h-11" data-testid="educator-rate-input" />
              </div>

              <div className="md:col-span-2">
                <Label className="text-[#0A2518]">{t("educatorDash.educationLabel")}</Label>
                <Input name="education" value={form.education} onChange={onChange} className="mt-1.5 h-11" data-testid="educator-education-input" />
              </div>

              <div>
                <Label className="text-[#0A2518] flex items-center gap-1.5"><Languages className="h-3.5 w-3.5" /> {t("educatorDash.languagesLabel")}</Label>
                <Input name="languages" value={form.languages} onChange={onChange} className="mt-1.5 h-11" placeholder={t("educatorDash.languagesPh")} data-testid="educator-languages-input" />
              </div>
              <div>
                <Label className="text-[#0A2518] flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {t("educatorDash.availabilityLabel")}</Label>
                <Input name="availability" value={form.availability} onChange={onChange} className="mt-1.5 h-11" placeholder={t("educatorDash.availabilityPh")} data-testid="educator-availability-input" />
              </div>

              {error && (
                <div className="md:col-span-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="md:col-span-2">
                <Button type="submit" disabled={saving} className="rounded-full h-12 px-7 bg-[#16A34A] hover:bg-[#15803D] text-white" data-testid="educator-save-btn">
                  <Save className="h-4 w-4 mr-2" />
                  {saving ? t("educatorDash.saveLoading") : t("educatorDash.saveCta")}
                </Button>
              </div>
            </form>
          </Card>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="p-6 border-[#E5EBE7]" data-testid="educator-bookings-card">
              <h2 className="font-display text-xl font-semibold text-[#0A2518] mb-4 flex items-center gap-2">
                <CalendarCheck className="h-5 w-5 text-[#16A34A]" /> {t("educatorDash.bookingsTitle")}
              </h2>
              {bookings.length === 0 ? (
                <p className="text-sm text-[#4B5563]">{t("educatorDash.bookingsEmpty")}</p>
              ) : (
                <div className="space-y-3">
                  {bookings.slice(0, 6).map((b) => (
                    <div key={b.id} className="rounded-xl border border-[#E5EBE7] p-3">
                      <div className="flex items-center justify-between">
                        <div className="font-medium text-[#0A2518] text-sm">{b.parent_name || "Parent"}</div>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${b.status === "PAID" ? "bg-[#F0FDF4] text-[#15803D]" : "bg-yellow-50 text-yellow-700"}`}>{b.status}</span>
                      </div>
                      <div className="text-xs text-[#4B5563] mt-1">{t("educatorDash.bookingsNet", { subject: b.subject, hours: b.hours, net: fmt(b.educator_net_xof) })}</div>
                    </div>
                  ))}
                </div>
              )}
            </Card>

            <Card className="p-6 border-[#E5EBE7]" data-testid="educator-requests-card">
              <h2 className="font-display text-xl font-semibold text-[#0A2518] mb-4 flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-[#16A34A]" /> {t("educatorDash.requestsTitle")}
              </h2>
              {requests.length === 0 ? (
                <p className="text-sm text-[#4B5563]">{t("educatorDash.requestsEmpty")}</p>
              ) : (
                <div className="space-y-4">
                  {requests.map((r) => (
                    <div key={r.id} className="rounded-xl border border-[#E5EBE7] p-4">
                      <div className="font-medium text-[#0A2518]">{r.parent?.name}</div>
                      <p className="text-sm text-[#4B5563] mt-1">{r.message}</p>
                      <div className="mt-2 flex items-center justify-between text-xs text-[#4B5563]">
                        <span>{new Date(r.created_at).toLocaleDateString("fr-FR")}</span>
                        <Badge className="bg-[#F0FDF4] text-[#15803D] border border-[#16A34A]/30">{r.status}</Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        </div>

        <SessionPointageDialog
          open={sessionDialogOpen}
          onOpenChange={setSessionDialogOpen}
          bookings={bookings.filter((b) => b.status === "PAID")}
          onCreated={() => setSessionsRefreshKey((k) => k + 1)}
        />
      </div>
    </div>
  );
}
